import { Layers, GitBranch, Network, EarthLock } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import FirewallVpnTunnels from "./FirewallVpnTunnels";
import FirewallVpnPhase1 from "./FirewallVpnPhase1";
import FirewallVpnPhase2 from "./FirewallVpnPhase2";
import SslVpnSettings from "./SslVpnSettings";

// ── Tab Config ────────────────────────────────────────────────────────────────
const TABS = [
    { id: "tunnels", label: "Tunnels", icon: Network, component: FirewallVpnTunnels },
    { id: "ssl_vpn_settings", label: "SSL VPN Settings", icon: EarthLock, component: SslVpnSettings },
    { id: "phase1", label: "IPSEC Phase 1", icon: Layers, component: FirewallVpnPhase1 },
    { id: "phase2", label: "IPSEC Phase 2", icon: GitBranch, component: FirewallVpnPhase2 },
] as const;

type TabId = typeof TABS[number]["id"];
const VALID_IDS = TABS.map(t => t.id) as string[];

// ── Main ──────────────────────────────────────────────────────────────────────
export default function VpnMain({ asset_id, client_id }: { asset_id: any, client_id: string }) {
    const [searchParams, setSearchParams] = useSearchParams();

    const rawTab = searchParams.get("vpn_tab");
    const activeTab: TabId = (rawTab && VALID_IDS.includes(rawTab) ? rawTab : "tunnels") as TabId;

    const setActiveTab = (id: TabId) => {
        setSearchParams(prev => {
            prev.set("vpn_tab", id);
            return prev;
        });
    };

    const ActiveComponent = TABS.find(t => t.id === activeTab)!.component;

    return (
        <div className="flex flex-col bg-transparent text-slate-800 dark:text-gray-200">

            {/* Tabs */}
            <div className="flex items-center gap-1 border-b border-slate-200 dark:border-gray-700 mb-4">
                {TABS.map(({ id, label, icon: Icon }) => {
                    const isActive = activeTab === id;
                    return (
                        <button
                            key={id}
                            onClick={() => setActiveTab(id)}
                            className={`relative flex items-center gap-1.5 px-4 py-2 text-[11px] font-bold uppercase tracking-widest transition-all
                                ${isActive
                                    ? "text-purple-600 dark:text-purple-400"
                                    : "text-slate-600 dark:text-gray-200 hover:text-slate-900 dark:hover:text-gray-300"
                                }`}
                        >
                            <Icon size={12} />
                            {label}
                            {isActive && (
                                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-500 rounded-t-full" />
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Tab Content */}
            <div className="animate-fade-in">
                <ActiveComponent asset_id={asset_id} client_id={client_id} />
            </div>
        </div>
    );
}
